import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Stack,
  Grid,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import api from '../../api';
import VideoExplanationPlayer from '../../components/VideoExplanationPlayer';
import { parseBlanks } from '../../utils/parseBlanks';
import HtmlWithLatex from '../QuestionBank/components/HtmlWithLatex';

// ── helpers ────────────────────────────────────────────────────────────────

const getAnswerType = (answer) => {
  if (answer.answer_type) return answer.answer_type;
  // Fallback detection for legacy answers
  if (answer.matched_pairs) return 'vocab_matching';
  if (answer.text_answer !== undefined) {
    const qt = (answer.question_type || '').toLowerCase();
    if (qt.includes('writing') || qt.includes('composition')) return 'writing';
    if (qt.includes('fill') && qt.includes('blank')) return 'fill_blank';
    if (qt.includes('short')) return 'short_answer';
  }
  return 'mcq';
};

const TYPE_LABELS = {
  mcq: { label: 'Multiple Choice', color: '#1e40af', bg: '#dbeafe' },
  fill_blank: { label: 'Fill in the Blank', color: '#0369a1', bg: '#e0f2fe' },
  short_answer: { label: 'Short Answer', color: '#92400e', bg: '#fef3c7' },
  vocab_matching: { label: 'Vocabulary Matching', color: '#065f46', bg: '#d1fae5' },
  writing: { label: 'Writing Composition', color: '#5b21b6', bg: '#ede9fe' },
};

const borderColorFor = (isCorrect) => {
  if (isCorrect === null || isCorrect === undefined) return '#f59e0b'; // writing → orange
  return isCorrect ? '#4caf50' : '#f44336';
};

const hasRenderableRichText = (html) => {
  const value = String(html || '');
  if (!value) return false;
  if (/<img\b[^>]*>/i.test(value)) return true;
  const text = value
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, '');
  return text.length > 0;
};

// ── sub-components ─────────────────────────────────────────────────────────

function McqOptions({ answer }) {
  if (!answer.options?.length) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>Options</Typography>
      <Stack spacing={1}>
        {answer.options.map((option) => {
          const isSelected = answer.selected_letter === option.letter;
          const isCorrectOpt = answer.correct_letter === option.letter;
          let borderColor = '#e0e0e0', bg = 'transparent', icon = null;
          if (isSelected && answer.is_correct)  { borderColor = '#4caf50'; bg = '#e8f5e9'; icon = <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />; }
          else if (isSelected && !answer.is_correct) { borderColor = '#f44336'; bg = '#ffebee'; icon = <CancelIcon sx={{ color: '#f44336', fontSize: 20 }} />; }
          else if (isCorrectOpt) { borderColor = '#81c784'; bg = '#f1f8f4'; }
          return (
            <Box key={option.option_id}
              sx={{ p: 1.5, borderRadius: 1.5, border: `2px solid ${borderColor}`, backgroundColor: bg, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0 }}>
                <Typography component="span" sx={{ fontWeight: 700 }}>{option.letter}.</Typography>
                <Box sx={{ minWidth: 0, '& p': { m: 0 } }}>
                  <HtmlWithLatex html={option.text || ''} />
                </Box>
              </Box>
              {icon}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

function TextAnswerBlock({ label, text, isCorrect, color }) {
  return (
    <Box sx={{
      p: 1.5, borderRadius: 2,
      border: `2px solid ${color}33`,
      bgcolor: `${color}11`,
    }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500, whiteSpace: 'pre-wrap' }}>
        {text || <em style={{ color: '#374151' }}>No answer submitted</em>}
      </Typography>
    </Box>
  );
}

function VocabMatchingReview({ answer }) {
  const pairs = answer.matched_pairs || [];
  const pairsCorrect = answer.pairs_correct ?? null;
  const pairsTotal = answer.pairs_total ?? pairs.length;
  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>Matched Pairs</Typography>
        {pairsCorrect !== null && (
          <Chip size="small" label={`${pairsCorrect}/${pairsTotal} correct`}
            color={pairsCorrect === pairsTotal ? 'success' : pairsCorrect > 0 ? 'warning' : 'error'}
            sx={{ fontWeight: 700 }} />
        )}
      </Stack>
      {pairs.length === 0 ? (
        <Typography variant="body2" color="text.secondary" fontStyle="italic">No pairs submitted.</Typography>
      ) : (
        <Stack spacing={0.75}>
          {pairs.map((pair, i) => {
            const correct = pair.is_correct ?? null;
            return (
              <Box key={i} sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                p: 1, borderRadius: 1.5, border: '1px solid',
                borderColor: correct === true ? '#a7f3d0' : correct === false ? '#fca5a5' : '#e5e7eb',
                bgcolor: correct === true ? '#f0fdf4' : correct === false ? '#fff1f2' : '#fafafa',
              }}>
                {correct === true && <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 18, flexShrink: 0 }} />}
                {correct === false && <CancelIcon sx={{ color: '#dc2626', fontSize: 18, flexShrink: 0 }} />}
                {correct === null && <HelpOutlineIcon sx={{ color: '#374151', fontSize: 18, flexShrink: 0 }} />}
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{pair.word_text || pair.word || `Word ${i + 1}`}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>→</Typography>
                <Typography variant="body2">{pair.definition_text || pair.definition || `Definition ${i + 1}`}</Typography>
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}

function AnswerComparison({ answer, type }) {
  if (type === 'mcq') {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2" color="text.secondary">
            Your Answer:{' '}
            <Typography component="span" sx={{ fontWeight: 700, color: answer.is_correct ? 'success.main' : 'error.main' }}>
              {answer.selected_letter || '—'}
            </Typography>
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2" color="text.secondary">
            Correct Answer:{' '}
            <Typography component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {answer.correct_letter || '—'}
            </Typography>
          </Typography>
        </Grid>
      </Grid>
    );
  }

  if (type === 'writing') {
    const minWords = answer.ai_metadata?.min_words ?? answer.writing_min_words ?? 75;
    const maxWords = answer.ai_metadata?.max_words ?? 100;
    const wCount = answer.writing_word_count ?? (answer.text_answer?.trim() ? answer.text_answer.trim().split(/\s+/).length : 0);
    const wMet = wCount >= minWords;
    const isGraded = answer.is_correct !== null && answer.is_correct !== undefined;
    return (
      <Stack spacing={1.5}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">Word count: {minWords}–{maxWords} words for full marks</Typography>
          {wCount > 0 && (
            <Chip
              label={wMet ? `${wCount} words ✓` : `${wCount} words (${minWords - wCount} short)`}
              size="small"
              color={wMet ? 'success' : isGraded ? 'error' : 'warning'}
              sx={{ fontWeight: 700 }}
            />
          )}
        </Box>
        <TextAnswerBlock label="Submitted Writing" text={answer.text_answer} isCorrect={isGraded ? answer.is_correct : null} color="#7c3aed" />
        {!isGraded && (
          <Alert severity="info" sx={{ py: 0.5, fontSize: '0.8rem' }}>
            Writing composition submitted — reviewed manually and does not affect your score.
          </Alert>
        )}
        {isGraded && !answer.is_correct && wCount > 0 && wCount < minWords && (
          <Alert severity="warning" sx={{ py: 0.5, fontSize: '0.8rem' }}>
            Your response is {minWords - wCount} word{minWords - wCount !== 1 ? 's' : ''} short of the {minWords}-word minimum.
          </Alert>
        )}
      </Stack>
    );
  }

  if (type === 'vocab_matching') {
    return null; // Shown above by VocabMatchingReview
  }

  if (type === 'fill_blank') {
    const correct = answer.is_correct;
    const studentAnswer = answer.text_answer || '';
    const correctAnswer = answer.correct_text || answer.correct_answer || '';
    const questionText = answer.question_text || answer.name || '';
    const parts = parseBlanks(questionText);
    const hasBlankToken = parts.some((p) => p.type === 'blank');

    const BlankToken = ({ word, isCorrect: tokenCorrect }) => (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          px: 1.5,
          py: 0.3,
          mx: 0.5,
          borderRadius: 1.5,
          fontWeight: 700,
          fontSize: '0.9rem',
          border: '2px solid',
          borderColor: tokenCorrect ? '#16a34a' : '#dc2626',
          bgcolor: tokenCorrect ? '#f0fdf4' : '#fff1f2',
          color: tokenCorrect ? '#15803d' : '#b91c1c',
        }}
      >
        {word || <em style={{ color: '#374151' }}>—</em>}
      </Box>
    );

    return (
      <Stack spacing={1.5}>
        {/* Student's sentence with blank filled in */}
        <Box sx={{ p: 1.5, borderRadius: 2, border: `2px solid ${correct ? '#bbf7d0' : '#fca5a5'}`, bgcolor: correct ? '#f0fdf4' : '#fff1f2' }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: correct ? '#16a34a' : '#dc2626', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 0.75 }}>
            Your Answer
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', lineHeight: 2 }}>
            {parts.map((part, idx) => {
              if (part.type === 'text') {
                return <Typography key={idx} component="span" variant="body2" sx={{ fontWeight: 500 }}>{part.value}</Typography>;
              }
              return <BlankToken key={idx} word={studentAnswer} isCorrect={correct} />;
            })}
            {!hasBlankToken && <BlankToken word={studentAnswer} isCorrect={correct} />}
          </Box>
        </Box>

        {/* Show correct answer when student was wrong */}
        {!correct && correctAnswer && (
          <Box sx={{ p: 1.5, borderRadius: 2, border: '2px solid #bbf7d0', bgcolor: '#f0fdf4' }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#16a34a', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 0.75 }}>
              Correct Answer
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', lineHeight: 2 }}>
              {parts.map((part, idx) => {
                if (part.type === 'text') {
                  return <Typography key={idx} component="span" variant="body2" sx={{ fontWeight: 500 }}>{part.value}</Typography>;
                }
                return <BlankToken key={idx} word={correctAnswer} isCorrect={true} />;
              })}
              {!hasBlankToken && <BlankToken word={correctAnswer} isCorrect={true} />}
            </Box>
          </Box>
        )}
      </Stack>
    );
  }

  // short_answer
  const correct = answer.is_correct;
  return (
    <Grid container spacing={1.5}>
      <Grid item xs={12} sm={6}>
        <TextAnswerBlock
          label="Your Answer"
          text={answer.text_answer}
          isCorrect={correct}
          color={correct ? '#16a34a' : '#dc2626'}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextAnswerBlock
          label="Correct Answer"
          text={answer.correct_text || answer.correct_answer}
          isCorrect={true}
          color="#16a34a"
        />
      </Grid>
    </Grid>
  );
}

// ── main component ─────────────────────────────────────────────────────────

const WorkSheetReview = () => {
  const { testId, attemptId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const role = (localStorage.getItem('role') || '').toLowerCase();
  const isParent = role === 'parent';
  const prefix = isParent ? 'parent' : 'student';
  const basePath = isParent ? '/parent/ai-tests' : '/student/ai-tests';
  const studentIdFromNav = isParent ? location.state?.studentId : undefined;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [testMeta, setTestMeta] = useState(null);
  const [uploadData, setUploadData] = useState(null);

  useEffect(() => {
    const handleExamModeChanged = () => {
      window.location.reload();
    };
    window.addEventListener("exam-mode-changed", handleExamModeChanged);
    return () => window.removeEventListener("exam-mode-changed", handleExamModeChanged);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resultsParams = { include_questions: 1 };
      if (studentIdFromNav) resultsParams.student_id = studentIdFromNav;
      const retakeParams = studentIdFromNav ? { student_id: studentIdFromNav } : {};
      const [res, retakeRes] = await Promise.all([
        api.get(`${basePath}/${testId}/results`, { params: resultsParams }),
        api.get(`${basePath}/${testId}/retake-history`, { params: retakeParams }).catch(() => null),
      ]);
      const data = res?.data?.data || {};
      const results = data?.results || [];
      const meta = data?.test || {};
      const found = results.find((r) => String(r.id) === String(attemptId));
      if (found) {
        setAttempt(found);
        setTestMeta(meta);
        const rh = retakeRes?.data?.data || retakeRes?.data || {};
        const ud = (rh?.by_upload_attempt || []).find(
          (u) => u.upload_attempt_count === found.attempt_count
        );
        setUploadData(ud || null);
      } else {
        setError('Attempt not found.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load review');
    } finally {
      setLoading(false);
    }
  }, [basePath, testId, attemptId, studentIdFromNav]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const makeAbsoluteUrl = (url) => {
    if (!url || typeof url !== 'string') return url;
    const trimmed = url.trim();
    if (!trimmed) return trimmed;
    if (/^data:/i.test(trimmed) || /^blob:/i.test(trimmed)) return trimmed;
    const baseURL = (api.defaults.baseURL || '').replace(/\/$/, '');
    const r2Match = trimmed.match(/^https?:\/\/pub-[^/]+\.r2\.dev\/(.+)$/);
    if (r2Match) return baseURL ? `${baseURL}/proxy/visual/${r2Match[1]}` : trimmed;
    if (trimmed.startsWith('//')) return `https:${trimmed}`;
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
    if (!baseURL) return trimmed;
    return trimmed.startsWith('/') ? `${baseURL}${trimmed}` : `${baseURL}/${trimmed}`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (error || !attempt) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Card sx={{ p: 4, textAlign: 'center', maxWidth: 440 }}>
          <Typography variant="h6" gutterBottom color="text.secondary">Review unavailable</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {error || 'This attempt could not be found.'}
          </Typography>
          <Button variant="contained" onClick={() => navigate(`/${prefix}/ai-answer-sheet`, { state: { openMyResults: true } })}>
            Back to Work Sheets
          </Button>
        </Card>
      </Box>
    );
  }

  const { summary = {}, answers = [] } = attempt;
  const percentage = summary.percentage ?? 0;
  const correct = summary.correct_answers ?? 0;
  const gradableTotal = summary.gradable_total ?? summary.total_questions ?? answers.length;
  const writingCount = summary.writing_count ?? 0;
  const cumulativePct = uploadData?.cumulative_percentage ?? percentage;
  const cumulativeCorrect = uploadData?.cumulative_correct_answers ?? correct;
  const swapeCorrected = uploadData?.swape_corrected_count || 0;
  const hasSweepImprovement = swapeCorrected > 0;
  const formatScoreNumber = (value) => {
    const n = Number(value ?? 0);
    if (!Number.isFinite(n)) return '0';
    return Number.isInteger(n) ? String(n) : n.toFixed(2);
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Button
        startIcon={<ArrowBackIcon />}
        size="small"
        sx={{ mb: 2 }}
        onClick={() => navigate(`/${prefix}/ai-answer-sheet`, { state: { openMyResults: true } })}
      >
        Back to Work Sheets
      </Button>

      {/* ── Score header ── */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
            <Box sx={{ width: 5, height: 40, borderRadius: 1, background: 'linear-gradient(180deg, #667eea, #764ba2)', mr: 1.5, flexShrink: 0 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {testMeta?.test_name || testMeta?.name || 'Work Sheet Review'}
              </Typography>
              {(testMeta?.subject || testMeta?.strand || testMeta?.subtopic) && (
                <Typography variant="body2" color="text.secondary">
                  {[testMeta?.subject, testMeta?.strand, testMeta?.subtopic].filter(Boolean).join(' › ')}
                </Typography>
              )}
            </Box>
          </Box>

          <Grid container spacing={2} alignItems="center" sx={{ flexDirection: { xs: "column", sm: "row" } }}>
            <Grid item xs={12} sm="auto">
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{
                  width: 100, height: 100, borderRadius: '50%',
                  background: cumulativePct >= 50
                    ? 'linear-gradient(135deg, #d1fae5, #6ee7b7)'
                    : 'linear-gradient(135deg, #fee2e2, #fca5a5)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  border: '3px solid', borderColor: cumulativePct >= 50 ? '#10b981' : '#ef4444',
                }}>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: cumulativePct >= 50 ? '#065f46' : '#991b1b', lineHeight: 1 }}>
                    {formatScoreNumber(cumulativePct)}%
                  </Typography>
                  <Typography variant="caption" sx={{ color: cumulativePct >= 50 ? '#047857' : '#b91c1c', fontWeight: 600 }}>
                    {hasSweepImprovement ? 'Cumulative' : 'Score'}
                  </Typography>
                </Box>
                {hasSweepImprovement && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Original: {formatScoreNumber(percentage)}%
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs>
              <Grid container spacing={2}>
                {(() => {
                  const gradableMarks = summary.gradable_marks ?? null;
                  const totalMarks = summary.total_marks ?? null;
                  return [
                    { label: 'Correct', value: hasSweepImprovement ? cumulativeCorrect : correct, color: '#2e7d32', bg: '#e8f5e9', border: '#a5d6a7' },
                    { label: 'Incorrect', value: gradableTotal - (hasSweepImprovement ? cumulativeCorrect : correct), color: '#c62828', bg: '#ffebee', border: '#ef9a9a' },
                    gradableMarks != null
                      ? { label: 'Gradable Marks', value: gradableMarks, color: '#374151', bg: '#f3f4f6', border: '#d1d5db' }
                      : { label: 'Gradable', value: gradableTotal, color: '#374151', bg: '#f3f4f6', border: '#d1d5db' },
                    ...(totalMarks != null && totalMarks !== gradableMarks
                      ? [{ label: 'Total Marks', value: totalMarks, color: '#374151', bg: '#f0fdf4', border: '#bbf7d0' }]
                      : []),
                  ];
                })().map(({ label, value, color, bg, border }) => (
                  <Grid item xs={6} sm={4} md={3} key={label}>
                    <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 2, bgcolor: bg, border: '1px solid', borderColor: border }}>
                      <Typography variant="h5" sx={{ fontWeight: 800, color }}>{formatScoreNumber(value)}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{label}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
              {writingCount > 0 && (
                <Alert severity="info" sx={{ mt: 1.5, py: 0.5, fontSize: '0.8rem' }}>
                  {writingCount} writing composition{writingCount > 1 ? 's' : ''} submitted — reviewed manually, not included in score.
                </Alert>
              )}
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5} sx={{ mt: 1.5 }}>
                {attempt.attempt_count && (
                  <Chip label={`Attempt ${attempt.attempt_count}`} color="primary" size="small" sx={{ fontWeight: 700 }} />
                )}
                {hasSweepImprovement && (
                  <Chip label={`+${swapeCorrected} via Sweep`} color="success" size="small" sx={{ fontWeight: 700 }} />
                )}
                {attempt.engine && (
                  <Chip label={attempt.engine} size="small" color="secondary" sx={{ fontWeight: 600 }} />
                )}
                {attempt.answer_sheet_url && (
                  <Button href={attempt.answer_sheet_url} target="_blank" rel="noreferrer" size="small" variant="outlined" sx={{ borderRadius: 2 }}>
                    Open Submitted Sheet
                  </Button>
                )}
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ── Questions ── */}
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Question Review {answers.length > 0 ? `(${answers.length} questions)` : ''}
      </Typography>

      {answers.length === 0 ? (
        <Alert severity="info">No question data available for this attempt.</Alert>
      ) : (
        <Stack spacing={2}>
          {answers.map((answer, idx) => {
            const type = getAnswerType(answer);
            const typeMeta = TYPE_LABELS[type] || TYPE_LABELS.mcq;
            const qNum = answer.displayNumber ?? idx + 1;
            const visualUrl = answer.visual_image_url || answer.image_url || null;
            const absVisualUrl = visualUrl ? makeAbsoluteUrl(visualUrl) : null;
            const isWriting = type === 'writing';
            const isVocab = type === 'vocab_matching';

            return (
              <Card
                key={answer.question_id}
                variant="outlined"
                sx={{
                  borderRadius: 2, border: '2px solid',
                  borderColor: borderColorFor(isWriting ? null : answer.is_correct),
                }}
              >
                <CardContent>
                  {/* Header row */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Chip label={`Q${qNum}`} color="primary" size="small" sx={{ fontWeight: 700 }} />
                      {/* Question type badge */}
                      <Chip
                        label={typeMeta.label}
                        size="small"
                        sx={{ fontWeight: 600, bgcolor: typeMeta.bg, color: typeMeta.color, border: `1px solid ${typeMeta.color}33` }}
                      />
                      {/* Marks chip */}
                      {answer.marks != null && (
                        <Chip
                          label={isVocab
                            ? `${answer.pairs_total ?? '?'} marks (pairs)`
                            : `${answer.marks} mark${answer.marks !== 1 ? 's' : ''}`}
                          size="small"
                          sx={{ fontWeight: 600, bgcolor: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' }}
                        />
                      )}
                      {answer.source === 'sweep' && (
                        <Chip label="via Sweep" size="small" color="warning" variant="outlined" sx={{ fontWeight: 600 }} />
                      )}
                      {(answer.subject || answer.strand || answer.subtopic) && (
                        <Chip
                          label={[answer.subject, answer.strand, answer.subtopic].filter(Boolean).join(' › ')}
                          size="small"
                          variant="outlined"
                          sx={{ maxWidth: 260, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                        />
                      )}
                    </Stack>
                    {/* Status chip */}
                    {isWriting ? (
                      <Chip
                        label={answer.attempted === false ? 'Not Attempted' : 'Submitted'}
                        sx={{ fontWeight: 700, bgcolor: '#fef3c7', color: '#92400e', border: '1px solid #f59e0b' }}
                      />
                    ) : answer.attempted === false ? (
                      <Chip
                        label="Not Attempted"
                        icon={<HelpOutlineIcon />}
                        sx={{ fontWeight: 700, bgcolor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db' }}
                      />
                    ) : isVocab && answer.pairs_correct !== undefined ? (
                      <Chip
                        label={`${answer.pairs_correct ?? 0}/${answer.pairs_total ?? 0} pairs`}
                        color={answer.is_correct ? 'success' : 'warning'}
                        icon={answer.is_correct ? <CheckCircleIcon /> : <CancelIcon />}
                        sx={{ fontWeight: 700 }}
                      />
                    ) : (
                      <Chip
                        label={answer.is_correct ? 'Correct' : 'Incorrect'}
                        color={answer.is_correct ? 'success' : 'error'}
                        icon={answer.is_correct ? <CheckCircleIcon /> : <CancelIcon />}
                        sx={{ fontWeight: 700 }}
                      />
                    )}
                  </Stack>

                  {/* Question text */}
                  <Box sx={{ fontWeight: 600, mb: 1.5, '& p': { m: 0 } }}>
                    <HtmlWithLatex html={answer.question_text || '—'} />
                  </Box>

                  {/* Question image */}
                  {absVisualUrl && (
                    <Box sx={{ mb: 2 }}>
                      <img
                        src={absVisualUrl}
                        alt="Question visual"
                        style={{ maxWidth: '100%', maxHeight: 280, borderRadius: 6, border: '1px solid #e0e0e0', display: 'block' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </Box>
                  )}

                  {/* Type-specific content */}
                  {type === 'mcq' && <McqOptions answer={answer} />}
                  {type === 'vocab_matching' && <VocabMatchingReview answer={answer} />}

                  {/* Answer comparison */}
                  <Divider sx={{ my: 1.5 }} />
                  <AnswerComparison answer={answer} type={type} />

                  {/* Explanation */}
                  {(hasRenderableRichText(answer.explanation) || answer.explanation_video_url) && (
                    <Box sx={{ mt: 2 }}>
                      <Divider sx={{ mb: 2 }} />
                      <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>Explanation</Typography>
                      {hasRenderableRichText(answer.explanation) && (
                        <Box
                          sx={{
                            color: 'text.secondary',
                            fontSize: '0.875rem',
                            lineHeight: 1.43,
                            mb: 1,
                            '& p': { m: 0 },
                            '& img': { maxWidth: '100%', height: 'auto', display: 'block', borderRadius: 1, my: 1 },
                          }}
                        >
                          <HtmlWithLatex html={answer.explanation} />
                        </Box>
                      )}
                      {answer.explanation_video_url && (
                        <VideoExplanationPlayer
                          videoUrl={makeAbsoluteUrl(answer.explanation_video_url)}
                          questionText={answer.question_text}
                          variant="button"
                        />
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default WorkSheetReview;
